import { useState } from 'react'

import NativeDropdown from '@/components/NativeDropdown'
import {
  nativeDropdownFlagGroupsMachine,
  nativeDropdownFlagGroupsTapton,
  nativeDropdownReceivingOption,
  nativeDropdownSaleType
} from '@/constants/fees'
import { useFlagGroup } from '@/contexts/flagsGroup'
import { useTonPlans } from '@/contexts/plans'
import { useReceivingOption } from '@/contexts/receivingOption'
import { useSaleTypeContext } from '@/contexts/saleType'
import { formatFeeToString, formatValueToString } from '@/helpers/fees'
import { usePlanFees } from '@/hooks/usePlanFees'
import { calculateNetSaleValue } from '@/utils/calculateNetSaleValue'
import { getSelectLabelByPlanMetadata } from '@/utils/getSelectLabelByPlanMetadata'

import CalculatorMachineLinkTo from '../CalculatorMachines/CalculatorMachineLinkTo'
import { AllTypeTabs } from '../CalculatorTabs'
import Input from '../Input'

type CalculatorMachineProps = {
  className?: string
}

const CalculatorTapTon: React.FC<CalculatorMachineProps> = () => {
  const { plansByType, currentPlan } = useTonPlans()
  const tierPlans = plansByType.get('tap_phone') || []
  const [selectedTier, setSelectedTier] = useState(() => {
    return tierPlans.findIndex((plan) => plan.isDefaultTier)
  })

  const currentPlanTier = tierPlans[selectedTier]

  const fees = usePlanFees(currentPlanTier?.name)
  const { flagsGroup, setFlagsGroup } = useFlagGroup()

  const { selectedSaleType, setSelectedSaleType } = useSaleTypeContext()
  const { receivingOptionText, receivingOption, setReceivingOption } =
    useReceivingOption()

  // Inicializa o valor da venda com R$ 100,00
  const [saleValueSelected, setSaleValueSelected] = useState(100)

  if (!fees || !currentPlan) return null

  const currentFee = fees[selectedSaleType]

  // Calcula o valor liquido da venda de acordo com os valores selecionados nos dropdowns
  const valueWithTonFeeCalculated = calculateNetSaleValue(
    currentFee,
    saleValueSelected
  )

  const selectOptions = tierPlans.map((plan, index) => {
    return {
      id: plan.name,
      value: index.toString(),
      label: getSelectLabelByPlanMetadata(plan)
    }
  })

  return (
    <div className="flex flex-col md:flex-row gap-24 lg:gap-0 md:items-center lg:p-0 px-24 pt-24 pb-40 w-full bg-display-0 rounded-16 rounded-tl-none shadow-sm">
      <div className="flex flex-col gap-16 lg:p-40 w-full lg:border-r">
        {tierPlans && tierPlans.length > 1 && (
          <div className="flex flex-col gap-8">
            <label className="mr-4 font-medium paragraph-16">
              Vendas Mensais
            </label>
            <NativeDropdown
              options={selectOptions}
              selectedOption={selectedTier}
              dropdownId="dropdown-select-promotion-tier"
              setStateValue={setSelectedTier}
              bodyClassName="border border-[0.1rem] border-display-400 flex flex-1 rounded-8 p-12 w-full bg-display-0"
              labelClassName="font-medium whitespace-nowrap !w-full !justify-between text-14"
              menuClassName="top-[44px] !w-[calc(100%+1px)] left-0 font-semibold"
            />
          </div>
        )}
        {/* Bandeira */}
        <div className="flex flex-col gap-8">
          <label
            htmlFor="dropdown-select-flags-group"
            className="font-medium paragraph-16"
          >
            Bandeiras
          </label>

          <div>
            <NativeDropdown
              options={
                currentPlan.type === 'tap_phone'
                  ? nativeDropdownFlagGroupsTapton
                  : nativeDropdownFlagGroupsMachine
              }
              selectedOption={flagsGroup}
              dropdownId="dropdown-select-flags-group"
              setStateValue={setFlagsGroup}
              bodyClassName="border border-[0.1rem] border-display-400 flex flex-1 rounded-8 p-12 w-full bg-display-0"
              labelClassName="font-medium whitespace-nowrap !w-full !justify-between ton-paragraph-16"
              menuClassName="top-[44px] !w-[calc(100%+1px)] left-0 font-semibold"
            />
          </div>
        </div>
        {/* Tipo da Venda */}
        <div className="flex flex-col gap-8">
          <label
            htmlFor="dropdown-select-sale-type"
            className="font-medium paragraph-16"
          >
            Tipo da Venda
          </label>

          <div>
            <NativeDropdown
              options={nativeDropdownSaleType}
              selectedOption={selectedSaleType}
              dropdownId="dropdown-select-sale-type"
              setStateValue={setSelectedSaleType}
              bodyClassName="border border-[0.1rem] border-display-400 flex flex-1 rounded-8 p-12 w-full bg-display-0"
              labelClassName="font-medium whitespace-nowrap !w-full !justify-between ton-paragraph-16"
              menuClassName="top-[44px] !w-[calc(100%+1px)] left-0 font-semibold"
            />
          </div>
        </div>
        {/* Recebimento da Venda */}
        <div className="flex flex-col gap-8">
          <label
            htmlFor="dropdown-select-receiving"
            className="font-medium paragraph-16"
          >
            Recebimento da Venda
          </label>

          <div>
            <NativeDropdown
              options={nativeDropdownReceivingOption}
              selectedOption={receivingOption}
              dropdownId="dropdown-select-receiving"
              setStateValue={setReceivingOption}
              bodyClassName="border border-[0.1rem] border-display-400 flex flex-1 rounded-8 p-12 w-full bg-display-0"
              labelClassName="font-medium whitespace-nowrap !w-full !justify-between ton-paragraph-16"
              menuClassName="top-[44px] !w-[calc(100%+1px)] left-0 font-semibold"
            />
          </div>
        </div>
        {/* Valor da Venda */}
        <div className="flex flex-col gap-8">
          <label
            htmlFor="input-sale-value"
            className="font-medium paragraph-16"
          >
            Valor da Venda
          </label>

          <div>
            <Input
              id="input-sale-value"
              name="value"
              defaultValue={saleValueSelected}
              handleinputsalevalue={setSaleValueSelected}
            />
          </div>
        </div>

        <CalculatorMachineLinkTo
          selectMode={AllTypeTabs.Tapton}
          planDetails={currentPlanTier}
          className="hidden md:block"
        />
      </div>

      <div className="flex flex-col gap-16 md:gap-24 lg:p-40 w-full">
        <div className="flex gap-4 items-center pb-16 border-b border-display-400 ton-paragraph-16">
          <span className="flex-grow">Você recebe</span>
          <span className="font-bold text-newTon-700 whitespace-nowrap">
            {formatValueToString(valueWithTonFeeCalculated)}
          </span>
        </div>

        <div className="flex gap-4 items-center pb-16 border-b border-display-400 ton-paragraph-16">
          <span className="flex flex-col flex-grow">
            {nativeDropdownSaleType[selectedSaleType].label}
          </span>

          <span className="font-bold whitespace-nowrap">
            {formatFeeToString(currentFee)}
          </span>
        </div>

        <div className="flex gap-4 items-center ton-paragraph-16">
          <span className="flex-grow">Recebimento da venda</span>
          <span className="font-bold whitespace-nowrap">
            {receivingOptionText}
          </span>
        </div>

        <span className="text-12 font-medium text-display-700">
          Taxas pra todos os dispositivos, Android e iOS. Para os dispositivos
          iOS, estão disponíveis apenas as bandeiras MasterCard, Visa e Amex
        </span>

        <CalculatorMachineLinkTo
          selectMode={AllTypeTabs.Tapton}
          className="md:hidden"
          planDetails={currentPlanTier}
        />
      </div>
    </div>
  )
}

export default CalculatorTapTon
